import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useStore } from 'react-redux'
import { useHistory } from 'react-router-dom'

import { PATHS } from '@/config/router/paths'
import type { AppDispatch } from '@/config/store/store'
import type { FetchCampaignsQueryParams } from '@/ducks/campaigns/api/campaigns.api'
import {
  getCampaignDisplayStatus
} from '@/ducks/campaigns/campaigns.helpers'
import type { Campaign, BuyXGetYCampaign, CampaignStatus, CartDiscountCampaign, FoodOfferCampaign } from '@/ducks/campaigns/campaigns.types'
import {
  patchCampaignStatusInCache,
  patchFoodOfferCampaignStatusInCache,
  removeCampaignFromCache,
  removeFoodOfferCampaignFromCache
} from '@/ducks/campaigns/campaigns.cache.helpers'
import {
  findBuyXGetYOfferByCampaign,
  getBuyXGetYDeleteOfferId,
  getBuyXGetYOfferId,
  isBuyXGetYOfferNotFoundError,
  mapBuyXGetYOfferToEditState
} from '@/ducks/campaigns/buyXGetYCampaign.helpers'
import {
  findCartDiscountByCampaign,
  getCartDiscountId,
  mapCartDiscountToEditState
} from '@/ducks/campaigns/cartDiscountCampaign.helpers'
import {
  findFoodOfferMenuItem,
  getFoodOfferFoodId
} from '@/ducks/campaigns/foodOfferCampaign.helpers'
import { addSystemNotification } from '@/ducks/notifications/slices/notification.slice'
import {
  useDeleteCartDiscountMutation,
  useDeleteOfferBuyXGetYMutation,
  useDisableOfferMutation,
  useFetchCartDiscountsQuery,
  useFetchOffersBuyXGetYQuery,
  useFetchOffersQuery,
  useUpdateBuyXGetYMutation,
  useUpdateCartDiscountMutation
} from '@/ducks/offers/api/offers.api'
import type { RootState } from '@/ducks/root.reducer'
import { useServerTranslation } from '@/hooks/useServerTranslation/useServerTranslation'

const CAMPAIGN_STATUS = {
  ACTIVE: 1,
  INACTIVE: 2
} as const

type Options = {
  onActionComplete?: () => void
  campaignsQueryParams?: FetchCampaignsQueryParams
}

const useCampaignActions = (campaign: Campaign | null, options: Options = {}) => {
  const { onActionComplete, campaignsQueryParams } = options
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const store = useStore()
  const history = useHistory()
  const { getTranslation } = useServerTranslation()
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [isDeactivateOpen, setIsDeactivateOpen] = useState(false)

  const [deleteCartDiscount] = useDeleteCartDiscountMutation()
  const [updateCartDiscount] = useUpdateCartDiscountMutation()
  const [deleteOfferBuyXGetY] = useDeleteOfferBuyXGetYMutation()
  const [updateBuyXGetY] = useUpdateBuyXGetYMutation()
  const [disableOffer] = useDisableOfferMutation()

  const { data: offersData } = useFetchOffersQuery(undefined, {
    skip: !campaign || campaign.campaignType !== 'food-offer'
  })

  const { data: buyXGetYOffers } = useFetchOffersBuyXGetYQuery(undefined, {
    skip: !campaign || campaign.campaignType !== 'buyXGetYOffer'
  })

  const { data: cartDiscounts } = useFetchCartDiscountsQuery(undefined, {
    skip: !campaign || campaign.campaignType !== 'cart_discount'
  })

  const getCartDiscountIdForCampaign = useCallback(
    (cartCampaign: CartDiscountCampaign) =>
      getCartDiscountId(cartCampaign, cartDiscounts),
    [cartDiscounts]
  )

  const getBuyXGetYOfferIdForCampaign = useCallback(
    (buyXGetYCampaign: BuyXGetYCampaign) =>
      getBuyXGetYOfferId(buyXGetYCampaign, buyXGetYOffers),
    [buyXGetYOffers]
  )

  const foodOfferMenuItem =
    campaign?.campaignType === 'food-offer'
      ? findFoodOfferMenuItem(
          offersData,
          getFoodOfferFoodId(campaign as FoodOfferCampaign),
          getTranslation
        )
      : null

  const getFoodOfferId = useCallback(
    (foodCampaign: FoodOfferCampaign) =>
      foodOfferMenuItem?.item._id ??
      foodCampaign.item?.id ??
      foodCampaign.campaignId,
    [foodOfferMenuItem]
  )

  const displayStatus = campaign ? getCampaignDisplayStatus(campaign) : 'inactive'
  const isExpired = displayStatus === 'expired'
  const isActive =
    campaign?.status === CAMPAIGN_STATUS.ACTIVE && !isExpired

  const getMoreActionsPrefix = useCallback(() => {
    if (!campaign) return 'offers.cart-discount.more-actions'
    return campaign.campaignType === 'buyXGetYOffer'
      ? 'offers.buyX-getY.more-actions'
      : 'offers.cart-discount.more-actions'
  }, [campaign])

  const removeDeletedCampaignFromList = useCallback(
    (campaignId: string) => {
      removeCampaignFromCache(
        dispatch as AppDispatch,
        store.getState as () => RootState,
        c => c.campaignId === campaignId,
        campaignsQueryParams
      )
    },
    [campaignsQueryParams, dispatch, store]
  )

  const handleEdit = useCallback(() => {
    if (!campaign) return

    onActionComplete?.()

    if (campaign.campaignType === 'cart_discount') {
      const cartCampaign = campaign as CartDiscountCampaign
      const discount = findCartDiscountByCampaign(cartDiscounts, cartCampaign)

      if (!discount) return

      const editState = mapCartDiscountToEditState(discount)

      history.push(PATHS.OFFERS_CART_DISCOUNT_CREATE, {
        state: editState
      })
      return
    }

    if (campaign.campaignType === 'buyXGetYOffer') {
      const buyXGetYCampaign = campaign as BuyXGetYCampaign
      const offer = findBuyXGetYOfferByCampaign(buyXGetYOffers, buyXGetYCampaign)

      if (!offer) return

      const editState = mapBuyXGetYOfferToEditState(offer)

      history.push(PATHS.OFFERS_BUYX_GETY_CREATE, {
        state: editState
      })
      return
    }

    const foodCampaign = campaign as FoodOfferCampaign
    const editState = {
      isEditing: true,
      offerId: getFoodOfferId(foodCampaign),
      initialDiscount:
        foodOfferMenuItem?.item.offer?.amount ?? foodCampaign.offerAmount,
      initialExpiration:
        foodOfferMenuItem?.item.offer?.offer_validity ??
        foodOfferMenuItem?.item.offer?.offer_valid_until ??
        foodCampaign.validUntil ??
        foodCampaign.offerValidity ??
        '',
      name: foodOfferMenuItem
        ? getTranslation(foodOfferMenuItem.item.name)
        : foodCampaign.item?.name ?? foodCampaign.name
    }

    history.push(PATHS.OFFERS_CREATE, {
      state: editState
    })
  }, [
    campaign,
    buyXGetYOffers,
    cartDiscounts,
    foodOfferMenuItem,
    getCartDiscountIdForCampaign,
    getFoodOfferId,
    getTranslation,
    history,
    onActionComplete
  ])

  const handleDeactivate = useCallback(async () => {
    if (!campaign) return

    try {
      if (campaign.campaignType === 'cart_discount') {
        const cartCampaign = campaign as CartDiscountCampaign
        const resolvedCartId = getCartDiscountIdForCampaign(cartCampaign)

        if (!resolvedCartId) {
          dispatch(
            addSystemNotification({
              message: t(
                `${getMoreActionsPrefix()}.deactivate-offer.error`
              ),
              intent: 'error'
            })
          )
          return
        }

        const payload = {
          status: isActive
            ? CAMPAIGN_STATUS.INACTIVE
            : CAMPAIGN_STATUS.ACTIVE,
          cartId: resolvedCartId
        }
        const result = await updateCartDiscount(payload).unwrap()

        if (result?.message === 'success') {
          patchCampaignStatusInCache(
            dispatch as AppDispatch,
            store.getState as () => RootState,
            campaign.campaignId,
            payload.status as CampaignStatus
          )
          dispatch(
            addSystemNotification({
              message: t(
                isActive
                  ? `${getMoreActionsPrefix()}.deactivate-offer.success`
                  : `${getMoreActionsPrefix()}.activate-offer.success`
              ),
              intent: 'success'
            })
          )
          onActionComplete?.()
        }
      } else if (campaign.campaignType === 'buyXGetYOffer') {
        const buyXGetYCampaign = campaign as BuyXGetYCampaign
        const resolvedOfferId = getBuyXGetYOfferIdForCampaign(buyXGetYCampaign)

        if (!resolvedOfferId) {
          dispatch(
            addSystemNotification({
              message: t('offers.buyX-getY.more-actions.deactivate-offer.error'),
              intent: 'error'
            })
          )
          return
        }

        const payload = {
          status: isActive
            ? CAMPAIGN_STATUS.INACTIVE
            : CAMPAIGN_STATUS.ACTIVE,
          offerId: resolvedOfferId
        }
        const result = await updateBuyXGetY(payload).unwrap()

        if (result?.message === 'success') {
          patchCampaignStatusInCache(
            dispatch as AppDispatch,
            store.getState as () => RootState,
            campaign.campaignId,
            payload.status as CampaignStatus
          )
          dispatch(
            addSystemNotification({
              message: t(
                isActive
                  ? 'offers.buyX-getY.more-actions.deactivate-offer.success'
                  : 'offers.buyX-getY.more-actions.activate-offer.success'
              ),
              intent: 'success'
            })
          )
          onActionComplete?.()
        }
      } else {
        const foodCampaign = campaign as FoodOfferCampaign
        const foodId = getFoodOfferFoodId(foodCampaign)
        await disableOffer([foodId]).unwrap()
        patchFoodOfferCampaignStatusInCache(
          dispatch as AppDispatch,
          store.getState as () => RootState,
          foodId,
          CAMPAIGN_STATUS.INACTIVE
        )
        dispatch(
          addSystemNotification({
            message: t('offers.disable-modal.title'),
            intent: 'success'
          })
        )
        onActionComplete?.()
      }
    } catch {
      const errorKey =
        campaign.campaignType === 'food-offer'
          ? 'offers.cart-discount.more-actions.deactivate-offer.error'
          : `${getMoreActionsPrefix()}.deactivate-offer.error`
      dispatch(
        addSystemNotification({
          message: t(errorKey),
          intent: 'error'
        })
      )
    } finally {
      setIsDeactivateOpen(false)
    }
  }, [
    campaign,
    buyXGetYOffers,
    cartDiscounts,
    disableOffer,
    dispatch,
    getBuyXGetYOfferIdForCampaign,
    getCartDiscountIdForCampaign,
    getFoodOfferId,
    getMoreActionsPrefix,
    isActive,
    onActionComplete,
    store,
    t,
    updateBuyXGetY,
    updateCartDiscount
  ])

  const handleDelete = useCallback(async () => {
    if (!campaign) return

    try {
      if (campaign.campaignType === 'cart_discount') {
        const resolvedCartId = getCartDiscountIdForCampaign(
          campaign as CartDiscountCampaign
        )

        if (!resolvedCartId) {
          dispatch(
            addSystemNotification({
              message: t('offers.cart-discount.more-actions.delete-offer.error'),
              intent: 'error'
            })
          )
          return
        }

        const result = await deleteCartDiscount(resolvedCartId).unwrap()
        if (result?.status === '1') {
          dispatch(
            addSystemNotification({
              message: t('offers.cart-discount.more-actions.delete-offer.success'),
              intent: 'success'
            })
          )
          removeDeletedCampaignFromList(campaign.campaignId)
          onActionComplete?.()
        }
      } else if (campaign.campaignType === 'buyXGetYOffer') {
        const buyXGetYCampaign = campaign as BuyXGetYCampaign
        const resolvedOfferId = getBuyXGetYDeleteOfferId(
          buyXGetYCampaign,
          buyXGetYOffers
        )

        if (!resolvedOfferId) {
          dispatch(
            addSystemNotification({
              message: t('offers.buyX-getY.more-actions.delete-offer.error'),
              intent: 'error'
            })
          )
          return
        }

        const result = await deleteOfferBuyXGetY(resolvedOfferId).unwrap()
        if (result?.status === '1') {
          dispatch(
            addSystemNotification({
              message: t('offers.buyX-getY.more-actions.delete-offer.success'),
              intent: 'success'
            })
          )
          removeDeletedCampaignFromList(campaign.campaignId)
          onActionComplete?.()
        }
      } else {
        const foodCampaign = campaign as FoodOfferCampaign
        const foodId = getFoodOfferFoodId(foodCampaign)
        await disableOffer([foodId]).unwrap()
        removeFoodOfferCampaignFromCache(
          dispatch as AppDispatch,
          store.getState as () => RootState,
          foodId
        )
        dispatch(
          addSystemNotification({
            message: t('offers.delete-offer'),
            intent: 'success'
          })
        )
        onActionComplete?.()
      }
    } catch (error) {
      if (
        campaign.campaignType === 'buyXGetYOffer' &&
        isBuyXGetYOfferNotFoundError(error)
      ) {
        dispatch(
          addSystemNotification({
            message: t('offers.buyX-getY.more-actions.delete-offer.success'),
            intent: 'success'
          })
        )
        removeDeletedCampaignFromList(campaign.campaignId)
        onActionComplete?.()
        return
      }

      const errorKey =
        campaign.campaignType === 'food-offer'
          ? 'offers.cart-discount.more-actions.delete-offer.error'
          : `${getMoreActionsPrefix()}.delete-offer.error`
      dispatch(
        addSystemNotification({
          message: t(errorKey),
          intent: 'error'
        })
      )
    } finally {
      setIsDeleteOpen(false)
    }
  }, [
    campaign,
    deleteCartDiscount,
    deleteOfferBuyXGetY,
    disableOffer,
    dispatch,
    getCartDiscountIdForCampaign,
    buyXGetYOffers,
    getFoodOfferId,
    getMoreActionsPrefix,
    onActionComplete,
    removeDeletedCampaignFromList,
    store,
    t
  ])

  const deactivateLabel =
    campaign?.campaignType === 'food-offer'
      ? t('offers.disable-offer')
      : isActive
        ? t(`${getMoreActionsPrefix()}.deactivate-offer.title`)
        : t(`${getMoreActionsPrefix()}.activate-offer.title`)

  const deactivateDescription =
    campaign?.campaignType === 'food-offer'
      ? t('offers.disable-modal.description', {
          name: foodOfferMenuItem
            ? getTranslation(foodOfferMenuItem.item.name)
            : (campaign as FoodOfferCampaign).item?.name ??
              (campaign as FoodOfferCampaign).name ??
              ''
        })
      : isActive
        ? t(`${getMoreActionsPrefix()}.deactivate-offer.description`)
        : t(`${getMoreActionsPrefix()}.activate-offer.description`)

  const deleteTitle = t('offers.delete-offer')
  const deleteDescription =
    campaign?.campaignType === 'food-offer'
      ? t('offers.disable-modal.description', {
          name: foodOfferMenuItem
            ? getTranslation(foodOfferMenuItem.item.name)
            : (campaign as FoodOfferCampaign).item?.name ??
              (campaign as FoodOfferCampaign).name ??
              ''
        })
      : t(`${getMoreActionsPrefix()}.delete-offer.description`)

  const isBuyXGetYOfferResolvable =
    campaign?.campaignType !== 'buyXGetYOffer' ||
    !!getBuyXGetYOfferIdForCampaign(campaign as BuyXGetYCampaign)

  const isDeactivateDisabled =
    !campaign ||
    isExpired ||
    (campaign.campaignType === 'food-offer' && !isActive) ||
    !isBuyXGetYOfferResolvable

  return {
    handleEdit,
    handleDeactivate,
    handleDelete,
    isExpired,
    isActive,
    isDeactivateDisabled,
    deactivateLabel,
    deactivateDescription,
    deleteTitle,
    deleteDescription,
    isDeleteOpen,
    setIsDeleteOpen,
    isDeactivateOpen,
    setIsDeactivateOpen
  }
}

export default useCampaignActions
